import { api } from "./api";
import type {
  GetICDCodesByInputArgs,
  ICDCodeResponse,
  PaginatedResponse,
  RedbookDrugProduct,
} from "types/DataTypes";

export type CPTCodeResult = {
  id: number;
  code: string;
  mod: string;
  description: string;
  is_internal: boolean;
};

export type CPTCodeSelectOption = {
  label: string;
  value: string;
  code: string;
  description: string;
  is_internal: boolean;
  id: number;
};

export const getCPTCodeSelectValue = (result: CPTCodeResult): string =>
  `${result.id}-${result.is_internal}`;

export const mapCPTCodeResultToSelectOption = (
  result: CPTCodeResult,
): CPTCodeSelectOption => ({
  label: `${result.code} - ${result.description}`,
  value: getCPTCodeSelectValue(result),
  code: result.code,
  description: result.description,
  is_internal: result.is_internal,
  id: result.id,
});

const getCPTCodeFallbackSelectValue = (
  code: string,
  description: string,
): string => `${code}::${description}`;

export const buildCPTCodeSelectedOption = (
  code: string,
  description: string,
  options: CPTCodeSelectOption[],
  label?: string,
): CPTCodeSelectOption | null => {
  if (!code) {
    return null;
  }

  const matched = options.find(
    (option) => option.code === code && option.description === description,
  );
  if (matched) {
    return matched;
  }

  return {
    label: label ?? `${code} - ${description}`,
    value: getCPTCodeFallbackSelectValue(code, description),
    code,
    description,
    is_internal: false,
    id: -1,
  };
};

type CodeSearchArgs = {
  code__istartswith?: string;
  description__icontains?: string;
  search?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
};

type RedbookSearchArgs = {
  search: string;
  limit?: number;
  offset?: number;
  ordering?: string;
  status?: "active" | "deactivated" | "all";
  exclude_repackagers?: string;
  form?: string;
  dosage?: string;
};

type RedbookUniqueNameSearchArgs = {
  search: string;
  status?: "active" | "deactivated" | "all";
  exclude_repackagers?: string;
  exclude_zero_prices?: string;
};

type RedbookUniqueProductNamesResponse = {
  results: string[];
};

// CPT code search API endpoint
export const cptCodeAPI = api.injectEndpoints({
  endpoints: (builder) => ({
    getCPTCodesByInput: builder.query<
      PaginatedResponse<CPTCodeResult>,
      CodeSearchArgs
    >({
      query: ({
        code__istartswith,
        description__icontains,
        search,
        ordering,
        limit = 25,
        offset = 0,
      }) => {
        const params: Record<string, string | number> = { limit, offset };
        // only add if the value is not null
        if (code__istartswith) params.code__istartswith = code__istartswith;
        if (description__icontains)
          params.description__icontains = description__icontains;
        if (search) params.search = search;
        if (ordering) params.ordering = ordering;

        return {
          url: "api/v1/cpt-codes/",
          method: "GET",
          params,
        };
      },
    }),
  }),
});

// DRG code search API endpoint
export const drgCodeAPI = api.injectEndpoints({
  endpoints: (builder) => ({
    getDRGCodesByInput: builder.query<
      PaginatedResponse<unknown>,
      CodeSearchArgs
    >({
      query: ({ code__istartswith, description__icontains, limit = 25 }) => {
        const params: Record<string, string | number> = { limit };
        // only add if the value is not null
        if (code__istartswith) params.code__istartswith = code__istartswith;
        if (description__icontains)
          params.description__icontains = description__icontains;

        return {
          url: "api/v1/drg-codes/",
          method: "GET",
          params,
        };
      },
    }),
  }),
});

// HCPCS code search API endpoint
export const hcpcsCodeAPI = api.injectEndpoints({
  endpoints: (builder) => ({
    getHCPCSCodesByInput: builder.query<
      PaginatedResponse<unknown>,
      CodeSearchArgs
    >({
      query: ({
        code__istartswith,
        description__icontains,
        search,
        ordering,
        limit = 25,
        offset = 0,
      }) => {
        const params: Record<string, string | number> = { limit, offset };
        // only add if the value is not null
        if (code__istartswith) params.code__istartswith = code__istartswith;
        if (description__icontains)
          params.description__icontains = description__icontains;
        if (search) params.search = search;
        if (ordering) params.ordering = ordering;

        return {
          url: "api/v1/hcpcs-codes/",
          method: "GET",
          params,
        };
      },
    }),
  }),
});

// ICD code search API endpoint
export const icdCodeAPI = api.injectEndpoints({
  endpoints: (builder) => ({
    getICDCodesByInput: builder.query<ICDCodeResponse, GetICDCodesByInputArgs>({
      query: ({
        icd_indicator,
        code__istartswith,
        description__icontains,
        limit = 25,
      }) => {
        const params: Record<string, string | number> = { limit };
        // only add if the value is not null
        if (icd_indicator) params.icd_indicator = icd_indicator;
        if (code__istartswith) params.code__istartswith = code__istartswith;
        if (description__icontains)
          params.description__icontains = description__icontains;

        return {
          url: "api/v1/icd-codes/",
          method: "GET",
          params,
        };
      },
    }),
  }),
});

// Anesthesia code search API endpoint
export const anesthesiaCodeAPI = api.injectEndpoints({
  endpoints: (builder) => ({
    getAnesthesiaCodesByInput: builder.query<
      PaginatedResponse<unknown>,
      CodeSearchArgs
    >({
      query: ({ code__istartswith, description__icontains, limit = 25 }) => {
        const params: Record<string, string | number> = { limit };
        if (code__istartswith) params.code__istartswith = code__istartswith;
        if (description__icontains)
          params.description__icontains = description__icontains;

        return {
          url: "api/v1/anesthesia-base-units/",
          method: "GET",
          params,
        };
      },
    }),
  }),
});

// redbook search API endpoint
export const redbookAPI = api.injectEndpoints({
  endpoints: (builder) => ({
    searchRedbookUniqueProductNames: builder.query<
      RedbookUniqueProductNamesResponse,
      RedbookUniqueNameSearchArgs
    >({
      query: ({
        search,
        status,
        exclude_repackagers,
        exclude_zero_prices = "true",
      }) => {
        const params: Record<string, string> = { search, exclude_zero_prices };
        if (status) {
          params.status = status;
        }
        if (exclude_repackagers) {
          params.exclude_repackagers = exclude_repackagers;
        }
        return {
          url: "api/v1/api/redbook/drug-products/unique-names/",
          method: "GET",
          params,
        };
      },
    }),
    searchRedbookDrugs: builder.query<
      PaginatedResponse<RedbookDrugProduct>,
      RedbookSearchArgs
    >({
      query: ({
        search,
        limit = 25,
        offset = 0,
        ordering,
        status,
        exclude_repackagers,
        form,
        dosage,
      }) => {
        const params: Record<string, string | number> = {
          search,
          limit,
          offset,
        };
        if (ordering) {
          params.ordering = ordering;
        }
        if (status) {
          params.status = status;
        }
        if (exclude_repackagers) {
          params.exclude_repackagers = exclude_repackagers;
        }
        if (form) {
          params.form = form;
        }
        if (dosage) {
          params.dosage = dosage;
        }
        return {
          url: "api/v1/api/redbook/drug-products/search/",
          method: "GET",
          params,
        };
      },
    }),
  }),
});

export const { useGetCPTCodesByInputQuery } = cptCodeAPI;
export const { useGetDRGCodesByInputQuery } = drgCodeAPI;
export const { useGetHCPCSCodesByInputQuery } = hcpcsCodeAPI;
export const { useGetICDCodesByInputQuery } = icdCodeAPI;
export const { useGetAnesthesiaCodesByInputQuery } = anesthesiaCodeAPI;
export const {
  useSearchRedbookDrugsQuery,
  useSearchRedbookUniqueProductNamesQuery,
} = redbookAPI;
